/**
 * Upload failures that explain themselves (fix-q1flow F44).
 *
 * The photo upload used to answer every failure with "Soubor se teď
 * nepodařilo nahrát. Zkus to prosím znovu." The file is rejected for its
 * FORMAT: expert_photo accepts png/jpeg/webp only (worker/src/attachments.ts,
 * ALLOWED_DECLARED), so a HEIC photo straight out of Apple Photos returns 415
 * invalid_file_type and "try again" could never work. Proved against the real
 * upload route in tests/fix-q1flow.test.ts: the same picture is stored as
 * JPEG (201) and refused as HEIC (415), with the R2 bucket bound either way,
 * so storage was never the cause. Every failure now names its reason.
 */

export const IMAGE_UPLOAD_MIME_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
/** brand_material uploads (design references, writing samples): images + PDF. */
export const BRAND_UPLOAD_MIME_TYPES = [...IMAGE_UPLOAD_MIME_TYPES, "application/pdf"] as const;
/** Mirrors MAX_ATTACHMENT_BYTES in worker/src/attachments.ts. */
export const MAX_UPLOAD_BYTES = 60 * 1024 * 1024;

export type UploadIssue =
  | "converted_photo"
  | "unsupported_type"
  | "too_large"
  | "quota"
  | "storage"
  | "offline"
  | "not_an_image"
  | "generic";

/** Apple and Android cameras hand out formats the pipeline cannot read yet. */
const CONVERTIBLE_PHOTO_EXTENSIONS = ["heic", "heif", "avif", "tif", "tiff", "dng", "raw", "cr2", "nef", "arw"];
const CONVERTIBLE_PHOTO_MIMES = [
  "image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence",
  "image/avif", "image/tiff", "image/x-adobe-dng"
];

export function fileExtension(name: string): string {
  const index = name.lastIndexOf(".");
  return index < 0 ? "" : name.slice(index + 1).toLowerCase();
}

/**
 * The checks we can make before spending the upload: a phone photo the server
 * will refuse, or a file over the hard byte cap.
 */
export function localUploadIssue(
  file: { name: string; type: string; size: number },
  accepted: readonly string[] = IMAGE_UPLOAD_MIME_TYPES,
  maxBytes = MAX_UPLOAD_BYTES
): UploadIssue | null {
  const declared = file.type.trim().toLowerCase().split(";", 1)[0];
  const extension = fileExtension(file.name);
  if (CONVERTIBLE_PHOTO_MIMES.includes(declared) || CONVERTIBLE_PHOTO_EXTENSIONS.includes(extension)) {
    return "converted_photo";
  }
  if (file.size > maxBytes) return "too_large";
  // An empty type happens on some browsers for a drag and drop; let the server
  // decide rather than blocking a file that would have gone through.
  if (declared && !accepted.includes(declared)) return "unsupported_type";
  return null;
}

export function uploadIssueFromResponse(status: number, error?: string): UploadIssue {
  // The file carries something the server reads as a document, not a picture
  // (an HTML page or a PDF renamed to .png). Its own message, because "wrong
  // format" would send the client back to re-export a file that is already a
  // PNG by name. A genuine photo with C2PA or XMP metadata no longer lands
  // here - the server sniffs the magic bytes first (F62).
  if (error === "active_content_rejected") return "not_an_image";
  if (error === "invalid_file_type" || status === 415) return "unsupported_type";
  if (error === "file_too_large" || status === 413) return "too_large";
  if (error === "quota_exceeded" || status === 429) return "quota";
  if (error === "storage_not_ready" || error === "storage_unavailable" || status === 503) return "storage";
  return "generic";
}

/**
 * Both upload surfaces speak the same reasons in their own nouns: the
 * questionnaire says "fotka", the beta form says "screenshot". Pass the
 * namespace that owns the copy; the issue keys are identical in each.
 */
export function uploadIssueLocaleKey(issue: UploadIssue, namespace = "questionnaire.upload"): string {
  return `${namespace}.${issue}`;
}

/** Reasons the browser can name for a failed fetch, before any server answer. */
export function uploadIssueFromNetwork(online: boolean): UploadIssue {
  return online ? "generic" : "offline";
}

/** One refused file of a batch: which one, and the named reason (F44 per file). */
export interface UploadFailure {
  name: string;
  issue: UploadIssue;
}

export interface UploadBatchResult {
  added: Array<{ id: string; name: string }>;
  failures: UploadFailure[];
}

/**
 * F84: the ONE batch engine behind every multi-file field. Each file is
 * judged on its own - a refused file lands in `failures` under its NAME with
 * its F44 issue and never stops the rest of the batch. The UI renders the
 * result; no surface re-implements this loop.
 */
export async function uploadAttachmentBatch(
  files: readonly File[],
  options: {
    scope: string;
    csrfToken: string;
    accepted?: readonly string[];
    fetchImpl?: (url: string, init: RequestInit) => Promise<Response>;
    online?: () => boolean;
    onProgress?(done: number, total: number): void;
  }
): Promise<UploadBatchResult> {
  const accepted = options.accepted ?? IMAGE_UPLOAD_MIME_TYPES;
  const fetchImpl = options.fetchImpl ?? ((url, init) => fetch(url, init));
  const online = options.online ?? (() => (typeof navigator === "undefined" ? true : navigator.onLine));
  const added: Array<{ id: string; name: string }> = [];
  const failures: UploadFailure[] = [];
  let done = 0;
  for (const file of files) {
    done += 1;
    options.onProgress?.(done, files.length);
    const localIssue = localUploadIssue(file, accepted);
    if (localIssue) {
      failures.push({ name: file.name, issue: localIssue });
      continue;
    }
    try {
      const form = new FormData();
      form.set("scope", options.scope);
      form.set("file", file);
      const response = await fetchImpl("/api/attachments", {
        method: "POST",
        credentials: "include",
        headers: { "X-Launchpad-CSRF": options.csrfToken },
        body: form
      });
      const body = await response.json().catch(() => ({})) as { attachmentId?: string; error?: string };
      if (!response.ok || !body.attachmentId) {
        failures.push({ name: file.name, issue: uploadIssueFromResponse(response.status, body.error) });
        continue;
      }
      added.push({ id: body.attachmentId, name: file.name });
    } catch {
      failures.push({ name: file.name, issue: uploadIssueFromNetwork(online()) });
    }
  }
  return { added, failures };
}
